import { Card, CardContent, Switch, View } from "@/components/ui";
import { CreditCard } from "@/components/shared/icons";
import { useAuthState } from "@/store/auth";
import { useState } from "react";
import { useUpdateClientPreferencesStore } from "@/store/profile/updateClientPreferences";
import { getErrorMessage } from "@/lib/helpers/string";
import { showToast } from "@/components/ui/toast/show-toast";
import { useRoute } from "../../router";
import { Typography } from "../../typography";
import { useClientStore } from "@/store/auth/client";
import { StripeCardBrandsEnum } from "@/__generated__/graphql";

export function AutoPayWidget() {
  const {
    data: {
      userInfo: { id, autoPay, paymentMethod },
    },
    setUserInfo,
  } = useAuthState();
  const { push } = useRoute();
  const { fetch: updateClientPreferences, loading } =
    useUpdateClientPreferencesStore();
  const { fetch: client, data } = useClientStore();

  const clientId = id;
  const [enableAutoPay, setEnableAutoPay] = useState(autoPay);

  const handleAutoPay = async (value: boolean) => {
    if (!paymentMethod) {
      push({ pageKey: "AddCreditCard" });
      return;
    }

    if (paymentMethod.brand === StripeCardBrandsEnum.Visa && value) {
      showToast({
        title:
          "We're currently unable to accept Visa for Auto Pay. Please switch to another card provider or save on fees by using PayNow.",
        type: "error",
      });
      return;
    }

    setEnableAutoPay(value);
    try {
      const updatePreferences = await updateClientPreferences({
        requestPayload: {
          input: {
            clientId,
            preferences: { autoPay: value },
          },
        },
      });

      if (updatePreferences.error) {
        throw new Error(updatePreferences.error);
      }

      client({ requestPayload: { id: clientId } });

      if (data) {
        setUserInfo(data?.userInfo);
      }
    } catch (updatePreferencesError) {
      const errMsg = getErrorMessage(
        updatePreferencesError,
        "Error updating client preferences",
      );
      showToast({ title: errMsg, type: "error" });
    }
  };

  return (
    <Card variant="info" className="w-full">
      <CardContent className="flex flex-col items-start gap-2 md:gap-3">
        <View className="flex w-full flex-row justify-between gap-2">
          <View className="flex flex-row items-center gap-2">
            <CreditCard weight="fill" className="text-primary" size={28} />
            <Typography variant="body-lg">Auto Pay</Typography>
          </View>
          <Switch
            checked={enableAutoPay}
            onCheckedChange={handleAutoPay}
            disabled={loading}
          />
        </View>
        <Typography variant="body-sm" color="foreground-intermediate">
          Enjoy hassle-free payments! Your card will be charged 14 days after
          each service is completed.
        </Typography>
      </CardContent>
    </Card>
  );
}
